import { useCallback, useEffect, useRef, type RefObject } from 'react';
import type { VirtuosoHandle } from 'react-virtuoso';

export interface SearchHighlightScrollOption {
  fullPath: string[];
}

/**
 * Scrolls the Virtuoso list so the combobox-highlighted search option is visible,
 * without fighting an in-progress wheel scroll (which would feel janky or snap back).
 */
export const useVirtuosoSearchHighlightScroll = (
  virtuosoRef: RefObject<VirtuosoHandle | null>,
  highlightedValue: string | null,
  options: readonly SearchHighlightScrollOption[],
): { handleVirtuosoScrolling: (scrolling: boolean) => void } => {
  const prevHighlightRef = useRef<string | null>(null);
  const isVirtuosoScrollingRef = useRef(false);

  useEffect(() => {
    if (
      !highlightedValue ||
      options.length === 0 ||
      highlightedValue === prevHighlightRef.current
    ) {
      return;
    }

    const index = options.findIndex(
      (opt) => opt.fullPath.join('/') === highlightedValue,
    );
    prevHighlightRef.current = highlightedValue;
    if (index < 0) return;

    if (isVirtuosoScrollingRef.current) {
      return;
    }

    virtuosoRef.current?.scrollIntoView({ index, behavior: 'auto' });
  }, [highlightedValue, options, virtuosoRef]);

  const handleVirtuosoScrolling = useCallback((scrolling: boolean) => {
    isVirtuosoScrollingRef.current = scrolling;
  }, []);

  return { handleVirtuosoScrolling };
};
